<template>
	<div class="menu-settings-editor">
		<ModalHeader
			:title="title"
			:button-text="getString('save')"
			@click-button="saveClicked" >
		</ModalHeader>
		<div class="advanced-options">
			<AppList :items="items" >
				<template v-slot:button="{item}">
					<div class="row centered space-between p-10">
						<p class="list-item-title">{{ item.title }}</p>
						<AppButton @click="item.callback()" small outline :text="item.title" :disabled="item.hasOwnProperty('disabled') ? item.disabled : false" />
					</div>
				</template>
				<template #text-slot="{item}">
					<div class="row centered space-between text-slot">
						<p class="list-item-title">{{ item.title }}</p>
						<AppTextInput :textarea="item.textarea" :value="item.value" :ref="item.ref" :placeholder="item.placeholder" @changed-text="item.callback" />
					</div>
				</template>
				<template #rooms-availability="{item}">
					<div class="row centered space-between text-slot">
						<p class="list-item-title">{{ getString('rooms_available', [userStore.menuWord]) }}</p>
						<AppChecklist
							:title="getString('rooms_available', [userStore.menuWord])"
							:values="roomsTitles"
							:selected-indexes="selectedRoomsIndexes"
							class="input-item"
							@change-checked="checkRoom"
						/>
					</div>
				</template>
				<template #slug-slot="{item}">
					<div class="row centered space-between text-slot">
						<p class="list-item-title">{{ item.title }}</p>
						<div class="text-input-container">
							<AppTextInput :value="item.value" :placeholder="item.placeholder" @changed-text="item.callback" @changing-text="sanitizeSlug" @focusout="setUniqueSlug" />
							<AppInPlaceCloud @close-cloud="closeSlugCloud" :type="-2" :active="activeWarningCloud">
								<p>{{ getString('product_url_warn') }}</p>
							</AppInPlaceCloud>
						</div>
					</div>
				</template>
				<template #color-slot="{item}">
					<div class="row centered space-between color-slot">
						<p class="list-item-title">{{ item.title }}</p>
						<AppColorPicker :ref="item.ref" :value="item.color" @changed-color="item.callback" />
					</div>
				</template>
				<template #image-slot="{item}">
					<div class="row centered space-between image-slot">
						<p class="list-item-title">{{ item.title }}</p>
						<AppDropzone
							:max-bytes="400 * 1024"
							:max-width="1024"
							:max-height="1024"
							:files="item.image ? [item.image] : []" @removed-file="menu.picture = null" @upload-complete="item.callback" />
					</div>
				</template>
			</AppList>
		</div>

		<AppPopup
			:title="getString('confirm_delete_menu', [userStore.menuWord])"
			:body-text="getString('confirm_delete_menu_explain', [userStore.menuWord])"
			:buttons="popupDeleteButtons"
			ref="confirm-delete"
		/>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="sortingBottomSheet" @toggling-popup="toggleSortingSheet">
			<MenuSorter @close="sortingBottomSheet = false" v-if="sortingBottomSheet" />
		</AppBottomSheet>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppColorPicker from "@/shared/components/AppColorPicker.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import MenuSorter from "@/console-new/components/config/menu/MenuSorter.vue";
import API from "@/shared/helpers/API";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		const roomsStore = useRoomsStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, menuStore, userStore, roomsStore};
	},
	name: "MenuSettingsEditor",
	emits: ["saved"],
	mixins: [WindowWidth],
	components: {MenuSorter, AppBottomSheet, AppInPlaceCloud, AppColorPicker, AppPopup, AppTextInput, AppButton, AppList, ModalHeader, AppDropzone, AppChecklist, AppSwitch},
	data() {
		return {
			activeWarningCloud: false,
			warningCloudTimeout: null,
			popupDeleteButtons: [
				{text: this.getString('cancel'), callback: null, negative: true},
				{text: this.getString('ok'), callback: () => {this.clickDelete()}},
			],
			sortingBottomSheet: false,
			menuRoomsExclude: [],
		};
	},
	computed: {
		roomsTitles() {
			let returningNames = [];
			if(this.roomsStore.roomsTables) {
				this.roomsStore.roomsTables.forEach(room => {
					returningNames.push(room.name);
				});
			}
			return returningNames;
		},
		selectedRoomsIndexes() {
			if(!this.roomsStore.roomsTables) {
				return [];
			}
			let returningIndexes = [];
			for(let i = 0; i < this.roomsStore.roomsTables.length; i++) {
				if(!this.menuRoomsExclude.includes(this.roomsStore.roomsTables[i].room_id)) {
					returningIndexes.push(i);
				}
			}
			return returningIndexes;
		},
		bottomSheetWidth() {
			if (this.windowWidth < 800) {
				return "100%";
			} else {
				return "550px";
			}
		},
		menu() {
			return this.menuStore.modMenu;
		},
		title() {
			return this.getString("title_settings", [this.menuStore.modMenu.name || this.userStore.menuWord]);
		},
		items() {
			let items = [
				{
					title: this.getString("menu_name", [this.userStore.menuWord]),
					customSlot: "text-slot",
					placeholder: this.getString("menu_name", [this.userStore.menuWord]),
					value: this.menu.name || "",
					textarea: false,
					callback: (value) => {this.menu.name = value}
				},
				{
					title: this.getString("menu_slug", [this.userStore.menuWord]),
					customSlot: "slug-slot",
					placeholder: this.getString("menu_slug_example"),
					value: this.menu.slug || "",
					callback: (value) => {this.menu.slug = value}
				},
				{
					title: this.getString("menu_color", [this.userStore.menuWord]),
					customSlot: "color-slot",
					placeholder: this.getString("menu_color", [this.userStore.menuWord]),
					color: this.menu.color || this.userStore.currBusinessColor || "#2352A2",
					ref: "color",
					callback: (value) => {this.menu.color = value.replace("#", "")}
				},
				{
					title: this.getString("menu_banner", [this.userStore.menuWord]),
					customSlot: "image-slot",
					explain: this.getString("suggested_res_1080_400"),
					image: this.menu ? this.menu.picture : null,
					callback: (files) => {this.menu.picture = files[0]}
				},
				{
					title: this.getString("counter_sales_available"),
					switch: true,
					active: this.menu.counter_available,
					callback: (value) => {
						this.menu.counter_available = value;
						let savedLastMenuId = localStorage.getItem("last_menu_id");
						if(!value && savedLastMenuId && this.menu.menu_id === savedLastMenuId) {
							localStorage.removeItem("last_menu_id");
						}
					}
				},
				{
					title: this.getString("available_online"),
					switch: true,
					active: this.menu.available,
					callback: (value) => {this.menu.available = value},
					dropdownSlot: 'rooms-availability',
				}
			];
			items.push({
				title: this.getString('pdf_version'),
				customSlot: 'button',
				callback: () => {

					let menuId = this.menuStore.modMenu.menu_id;
					let menuIdPart = menuId ? "&menu=" + menuId : "";
					let authPart = API.init().oauthToken ? "&t="+API.init().oauthToken.substring(0, 7) : "";
					let userPart = "u=" + this.userStore.user.user_id;
					if(this.userStore.currBusiness.link) {
						const protocol = ((TEST && LOCAL) ? "http://" : "https://");
						window.location.href = protocol + this.userStore.currBusiness.link + "." + HOST + "/pdf?" + userPart + authPart + menuIdPart;
					} else {
						this.notificationStore.showNotification("generic-fail");
					}
				},
				disabled: !this.userStore.currBusiness || !this.userStore.currBusiness.link || !this.userStore.currBusiness.plan_id,
			});
			items.push({
					title: this.getString('sort'),
					customSlot: 'button',
					callback: () => {this.sortingBottomSheet = true},
			});
			items.push({
					title: this.getString('delete'),
					customSlot: 'button',
					callback: () => {this.$refs["confirm-delete"].toggleStatus(true)},
			});
			return items;
		},
	},
	methods: {
		checkRoom(newCheckedIndexes) {
			let newRoomExcl = [];
			this.roomsStore.roomsTables.forEach((room, index) => {
				if(!newCheckedIndexes.includes(index)) {
					newRoomExcl.push(room.room_id);
				}
			});
			this.menuRoomsExclude = newRoomExcl;
		},
		toggleSortingSheet(isOpen) {
			if(!isOpen) {
				this.sortingBottomSheet = false;
			}
		},
		saveClicked() {
			this.menuStore.updateMenu(this.menu, this.menuRoomsExclude, () => {
				this.showNotification("generic-success");
				this.$emit("saved");
			});
		},
		clickDelete() {
			let savedLastMenuId = localStorage.getItem("last_menu_id");
			if(savedLastMenuId && this.menu.menu_id === savedLastMenuId) {
				localStorage.removeItem("last_menu_id");
			}
			this.menuStore.deleteMenu(this.menu.menu_id, (success, details) => {
				this.$refs["confirm-delete"].toggleStatus(false);
				if(success) {
					this.$emit("saved");
				} else {
					if(details === "last_menu") {
						this.showNotification("menu-delete-last", [this.userStore.menuWord]);
					} else {
						this.showNotification("generic-fail");
					}
				}
			});
		},
		replaceCharacter(text) {
			text = (text || "").toLowerCase();
			text = text.replaceAll(" ", "-");
			let regexList = [/[áàãâä]/gi, /[éèêë]/gi, /[íìîï]/gi, /[óòôõö]/gi, /[úùûü]/gi, /[ç]/gi, /[ñ]/gi, /\s/gi, /_/gi, /@/gi, /&/gi, /[^a-z0-9\-]/gi];
			let replaceList = ["a", "e", "i", "o", "u", "c", "n", "-", "-", "a", "e", "-"];
			for (let i = 0; i < regexList.length; i++) {
				text = text.replace(regexList[i], replaceList[i]);
			}
			text = text.normalize("NFD").toLowerCase();
			return text || null;
		},
		sanitizeSlug(value) {
			let originalText = (value || "").toLowerCase();
			this.menu.slug = this.replaceCharacter(value);
			if(this.menu.slug && this.menu.slug !== originalText) {
				this.activeWarningCloud = true;
				clearTimeout(this.warningCloudTimeout);
				this.warningCloudTimeout = setTimeout(() => {
					this.activeWarningCloud = false;
				}, 3000);
			}
		},
		setUniqueSlug() {
			this.sanitizeSlug(this.menu.slug);
			this.menu.slug = this.menuStore.getUniqueMenuSlug(this.menu.slug);
		},
		closeSlugCloud() {
			this.activeWarningCloud = true;
			clearTimeout(this.warningCloudTimeout);
		},
		getMenuRoomsExcluded() {
			this.menuRoomsExclude = [];
			API.init().getMenuRoomsExcluded(this.userStore.currBusinessId, this.menu.menu_id, (r) => {
				if(r.result && r.result === "OK") {
					this.menuRoomsExclude = r.rooms;
				}
			});
		},
	},
	mounted() {
		this.roomsStore.getBusinessRooms();
		this.getMenuRoomsExcluded();
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.roomsStore.getBusinessRooms();
				this.getMenuRoomsExcluded();
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.list-container {
	:deep(.list-item) {
		padding: 8px 0;
	}
	:deep(.list-item .list-item-header) {
		padding: 10px 0;
	}
	:deep(.list-item:first-of-type:before),
	:deep(.list-item:last-of-type:after) {
		display: none;
	}
	:deep(.dropdown-header) {
		@include centered;
		@include noSelect;
		justify-content: space-between;
		cursor: pointer;
		padding: 10px 0;
	}

	.color-slot .list-item-title {
		width: calc(100% - 45px);
	}

	.extra-text {
		p {
			font-size: 14px;
			color: #9B9B9B;
		}
	}

	.text-slot .input-wrapper {
		max-width: 40%;
		margin-left: auto;
	}

	.text-input-container {
		position: relative;
		margin-left: auto;

		.input-wrapper {
			max-width: unset;
			margin-left: 0;
		}
	}

	.divider-parent {
		padding-bottom: 10px;
		position: relative;

		.divider::after {
			@include pseudo;
			bottom: 0;
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-border-alpha);
		}
	}

	@media (max-width: 600px) {
		.text-slot {
			flex-direction: column;

			.list-item-title {
				width: 100%;
				max-width: unset;
			}

			.input-wrapper {
				max-width: unset;
			}
		}

	}
}

</style>